import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SummaryPanel = ({ formData, currentStep, onScheduleConsultation, onSaveDraft, onRequestCallback }) => {
  const getSelectedServices = () => {
    const services = [];
    
    if (formData.painPoints?.length > 0) {
      services.push({
        name: 'Análisis de Puntos de Dolor',
        description: `${formData.painPoints.length} áreas identificadas`,
        icon: 'AlertTriangle'
      });
    }
    
    if (formData.currentSoftware?.length > 0) {
      services.push({
        name: 'Auditoría de Herramientas',
        description: `${formData.currentSoftware.length} herramientas a evaluar`,
        icon: 'Settings'
      });
    }
    
    if (formData.departmentsUsingSystem?.length > 0) {
      services.push({
        name: 'Mapeo de Procesos',
        description: `${formData.departmentsUsingSystem.length} departamentos`,
        icon: 'GitBranch'
      });
    }

    return services;
  };

  const getEstimatedDuration = () => {
    const baseHours = 2;
    const additionalHours = Math.floor((formData.painPoints?.length || 0) / 3) + 
                           Math.floor((formData.currentSoftware?.length || 0) / 4);
    return Math.min(baseHours + additionalHours, 4);
  };

  const getNextSteps = () => {
    return [
      'Revisión inicial de información proporcionada',
      'Preparación de agenda de auditoría',
      'Sesión de consultoría diagnóstica',
      'Entrega de mapa de automatización',
      'Propuesta de Arquitectura IA Autónoma'
    ];
  };

  const selectedServices = getSelectedServices();
  const estimatedDuration = getEstimatedDuration();
  const nextSteps = getNextSteps();

  return (
    <div className="bg-white border border-black/10 rounded-2xl p-6 sticky top-24">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-black">Resumen de Consultoría</h3>
          <p className="text-sm text-black/50">Auditoría IA Personalizada</p>
        </div>
      </div>

      {/* Company Info */}
      {formData.companyName && (
        <div className="mb-6 p-4 bg-black/5 rounded-lg">
          <h4 className="font-medium text-black mb-2">{formData.companyName}</h4>
          <div className="space-y-1 text-sm text-black/50">
            {formData.industry && <p>Industria: {formData.industry}</p>}
            {formData.employeeCount && <p>Empleados: {formData.employeeCount}</p>}
            {formData.urgency && <p>Urgencia: {formData.urgency}</p>}
          </div>
        </div>
      )}

      {/* Selected Services */}
      {selectedServices.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-black mb-3">Servicios Incluidos</h4>
          <div className="space-y-3">
            {selectedServices.map((service, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={service.icon} size={16} className="text-[#7C3AED]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-black">{service.name}</p>
                  <p className="text-xs text-black/50">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duration */}
      <div className="mb-6 p-4 bg-[#7C3AED]/5 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Clock" size={16} className="text-[#7C3AED]" />
          <span className="font-medium text-black">Duración Estimada</span>
        </div>
        <p className="text-2xl font-semibold text-[#7C3AED]">{estimatedDuration} horas</p>
        <p className="text-sm text-black/50">Consultoría personalizada</p>
      </div>

      {/* Next Steps */}
      <div className="mb-6">
        <h4 className="font-medium text-black mb-3">Próximos Pasos</h4>
        <div className="space-y-2">
          {nextSteps.map((step, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-black/5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium text-black/60">{index + 1}</span>
              </div>
              <p className="text-sm text-black/50">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Value Proposition */}
      <div className="mb-6 p-4 bg-black/[0.03] border border-black/10 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Gift" size={16} className="text-black" />
          <span className="font-medium text-black">Consultoría Gratuita</span>
        </div>
        <p className="text-sm text-black/50">
          Valorada en $700 USD. Sin compromiso de compra.
        </p>
      </div>

      <div className="space-y-3">
        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={onScheduleConsultation}
          className="bg-black hover:opacity-80 text-white font-medium"
          iconName="Calendar"
          iconPosition="left"
          disabled={currentStep < 3}
        >
          Programar Consultoría
        </Button>

        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onSaveDraft}
            iconName="Save"
            iconPosition="left"
            className="border-black/20 text-black hover:bg-black/5"
          >
            Guardar
          </Button>
        </div>
      </div>

      {/* Contact Info */}
      <div className="mt-6 pt-6 border-t border-black/10">
        <p className="text-xs text-black/50 text-center mb-2">
          ¿Necesita ayuda?
        </p>
        <div className="flex justify-center space-x-4">
          <a
            href="mailto:contacto@orbiax.co"
            className="text-xs text-black hover:text-[#7C3AED] transition-colors"
          >
            contacto@orbiax.co
          </a>
          <a
            href="tel:+57328571202"
            className="text-xs text-black hover:text-[#7C3AED] transition-colors"
          >
            +57 3228571202
          </a>
        </div>
      </div>
    </div>
  );
};

export default SummaryPanel;